import { useEffect, useRef, useState } from 'react';
import { MAX_PAGE_SIZE, formatSort, taskSchema, type Task } from '@forecastify/shared';
import type { PagerState, SortState } from '@forecastify/ui';
import { parseList, totalOf } from '../permissions-hook';
import { readAll } from '../read-all';
import { pagerOf, usePages } from '../use-pages';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 31 Oct 2026 · A line item's tasks, paged on the server.                    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The route read EVERY task of the line item's project (`readAll`, up to 5,000) and handed the
 * screen the ones on this line item, which filtered, sorted and paged them in memory — and the
 * picker searched the rest. Now:
 *
 * - **the list** asks `/tasks?lineItemId=` for one page, with its filters and sort as parameters;
 * - **the footer** asks `/tasks/tracked-hours` with the same filters — a sum of one page would be
 *   a figure about rows nobody can see (`state-rules.md` §5);
 * - **the picker** searches the project on the server as somebody types;
 * - **the exclusions and the overlap** are resolved by id (`/tasks?ids=`), because they are by
 *   definition NOT on this line item's list.
 *
 * ⚠️ **Every filter change goes back to page one** — `use-pages.ts`'s trap.
 */

/** The list's page size — the one the in-memory pager used, kept. */
export const LINE_ITEM_TASK_PAGE = 20;

/** The picker's result size, as before. */
const PICKER_SIZE = 30;

/** The `assignee` filter's item for a task nobody holds. Not a member id, so it cannot collide. */
export const NOBODY = 'none';

/**
 * 5 Nov 2026 · the *Forrás* filter's item for the tasks this line item's rule was told to leave
 * alone — so somebody can see what was thrown back and put it back. Not a `line_item_source`.
 */
export const EXCLUDED = 'excluded';

interface Session {
  readonly workspaceId: string | null;
  readonly request: (path: string, init?: RequestInit) => Promise<Response>;
}

export interface LineItemTasks {
  readonly rows: readonly Task[];
  readonly page: PagerState;
  /** The filtered set's count and logged hours. `null` until they arrive. */
  readonly totals: { readonly count: number; readonly trackedHours: string } | null;
  readonly loading: boolean;
  readonly query: string;
  readonly sort: readonly SortState[];
  readonly status: string;
  readonly assignee: string;
  readonly source: string;
  readonly setQuery: (value: string) => void;
  readonly setSort: (value: readonly SortState[]) => void;
  readonly setStatus: (value: string) => void;
  readonly setAssignee: (value: string) => void;
  readonly setSource: (value: string) => void;
  /** The excluded and the overlapping tasks, by id — neither is on the list. */
  readonly linked: ReadonlyMap<string, Task>;
  /** The picker: what it searches for, and what the project has that matches. */
  readonly pickerQuery: string;
  readonly setPickerQuery: (value: string) => void;
  readonly pickerMatches: readonly Task[];
}

export function useLineItemTasks(
  session: Session,
  item: {
    readonly id: string;
    readonly projectId: string | null;
    readonly excludedTaskIds: readonly string[];
    readonly overlapTaskIds: readonly string[];
  } | null,
  /** The route's live counter — a task joining by rule re-reads the page. */
  liveNonce: number,
): LineItemTasks {
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const pages = usePages(LINE_ITEM_TASK_PAGE);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<readonly SortState[]>([]);
  const [status, setStatus] = useState('');
  const [assignee, setAssignee] = useState('');
  const [source, setSource] = useState('');
  const [rows, setRows] = useState<readonly Task[]>([]);
  const [totals, setTotals] = useState<LineItemTasks['totals']>(null);
  const [loading, setLoading] = useState(true);
  const [linked, setLinked] = useState<ReadonlyMap<string, Task>>(new Map());
  const [pickerQuery, setPickerQuery] = useState('');
  const [pickerMatches, setPickerMatches] = useState<readonly Task[]>([]);

  const itemId = item?.id ?? null;
  const projectId = item?.projectId ?? null;
  const settle = pages.settle;
  const reset = pages.reset;

  /**
   * ⚠️⚠️ 5 Nov 2026 · **THE EXCLUDED ARE NOT ON THIS LINE ITEM** — an excluded task is by definition
   * somewhere else, or nowhere — so *Kizárva* swaps `lineItemId` for the line item's exclusion ids
   * (`/tasks?ids=`). Search, the other filters, sort and paging apply to them as to any list.
   */
  const excludedIds = (item?.excludedTaskIds ?? []).join(',');

  /* ── the list and its totals ── */
  useEffect(() => {
    if (itemId === null) return;
    const excluded = source === EXCLUDED;
    /* ⚠️ No exclusions, nothing to ask: an empty `ids` is no filter at all, which is every task. */
    if (excluded && excludedIds === '') {
      setRows([]);
      settle(0);
      setTotals({ count: 0, trackedHours: '0' });
      setLoading(false);
      return;
    }
    const filters = new URLSearchParams(excluded ? { ids: excludedIds } : { lineItemId: itemId });
    if (query.trim() !== '') filters.set('search', query.trim());
    if (status !== '') filters.set('statusId', status);
    /* 3 Nov 2026 · a comma list, *Nincs felelős* (`none`) among them — the server reads it as
       "unassigned OR one of these" (R-62, amended). */
    if (assignee !== '') filters.set('assigneeMemberId', assignee);
    if (source !== '' && !excluded) filters.set('lineItemSource', source);
    const paged = new URLSearchParams(filters);
    paged.set('limit', String(LINE_ITEM_TASK_PAGE));
    if (pages.offset > 0) paged.set('offset', String(pages.offset));
    const order = formatSort(sort);
    if (order !== undefined) paged.set('sort', order);

    const run = { live: true };
    setLoading(true);
    void (async () => {
      const current = sessionRef.current;
      const [pageResponse, totalsResponse] = await Promise.all([
        current.request(`/tasks?${paged.toString()}`).catch(() => null),
        current.request(`/tasks/tracked-hours?${filters.toString()}`).catch(() => null),
      ]);
      const body: unknown =
        pageResponse?.ok === true ? await pageResponse.json().catch(() => null) : null;
      const sums: unknown =
        totalsResponse?.ok === true ? await totalsResponse.json().catch(() => null) : null;
      if (!run.live) return;
      setRows(parseList(taskSchema, body) ?? []);
      settle(totalOf(body));
      const shaped = sums as { count?: unknown; trackedHours?: unknown } | null;
      setTotals(
        typeof shaped?.count === 'number' && typeof shaped.trackedHours === 'string'
          ? { count: shaped.count, trackedHours: shaped.trackedHours }
          : null,
      );
      setLoading(false);
    })();
    return () => {
      run.live = false;
    };
  }, [itemId, query, status, assignee, source, sort, pages.offset, settle, liveNonce, excludedIds]);

  /* ⚠️ Another line item is another set — back to page one. */
  useEffect(() => {
    reset();
  }, [itemId, reset]);

  /* ── the exclusions and the overlap, by id ── */
  const ids = [...(item?.excludedTaskIds ?? []), ...(item?.overlapTaskIds ?? [])].join(',');
  useEffect(() => {
    if (ids === '') {
      setLinked(new Map());
      return;
    }
    const run = { live: true };
    void (async () => {
      const found = await readAll(
        sessionRef.current,
        `/tasks?ids=${encodeURIComponent(ids)}`,
        taskSchema,
      ).catch(() => null);
      if (run.live && found !== null) setLinked(new Map(found.map((task) => [task.id, task])));
    })();
    return () => {
      run.live = false;
    };
  }, [ids, liveNonce]);

  /* ── the picker: the project's tasks NOT on this line item, searched on the server ── */
  useEffect(() => {
    if (itemId === null || projectId === null) {
      setPickerMatches([]);
      return;
    }
    const run = { live: true };
    /* A short pause, so typing a word is one request rather than one per letter. */
    const timer = setTimeout(() => {
      /* ⚠️ A full page, then this line item's own dropped: there is no *not on this line item*
         filter, and thirty-one could all be ours on a busy item, leaving the picker empty. */
      const params = new URLSearchParams({ projectId, limit: String(MAX_PAGE_SIZE) });
      if (pickerQuery.trim() !== '') params.set('search', pickerQuery.trim());
      void (async () => {
        const response = await sessionRef.current
          .request(`/tasks?${params.toString()}`)
          .catch(() => null);
        const body: unknown =
          response?.ok === true ? await response.json().catch(() => null) : null;
        if (!run.live) return;
        setPickerMatches(
          (parseList(taskSchema, body) ?? [])
            .filter((task) => task.lineItemId !== itemId)
            .slice(0, PICKER_SIZE),
        );
      })();
    }, 200);
    return () => {
      run.live = false;
      clearTimeout(timer);
    };
  }, [itemId, projectId, pickerQuery, liveNonce]);

  const resetting =
    <T>(set: (value: T) => void) =>
    (value: T): void => {
      set(value);
      reset();
    };

  return {
    rows,
    page: pagerOf(pages),
    totals,
    loading,
    query,
    sort,
    status,
    assignee,
    source,
    setQuery: resetting(setQuery),
    setSort: resetting(setSort),
    setStatus: resetting(setStatus),
    setAssignee: resetting(setAssignee),
    setSource: resetting(setSource),
    linked,
    pickerQuery,
    setPickerQuery,
    pickerMatches,
  };
}
